import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, fromEvent, interval } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { switchMap, take } from 'rxjs/operators';

import {
  NewVersionConfirmationDialog,
} from '../../shared/components/new-version-confirmation-dialog/new-version-confirmation-dialog';

@Injectable()
export class PromptUpdateService {
  private swUpdate = inject(SwUpdate);
  private ngbModal = inject(NgbModal);
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);

  constructor() {
    // Proactively look for a new deployment so an open tab picks it up without a
    // manual refresh — on a steady interval and whenever the tab regains focus.
    if (this.swUpdate.isEnabled) {
      const checkForUpdate = () => this.swUpdate.checkForUpdate().catch(() => undefined);
      interval(60_000).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(checkForUpdate);
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(checkForUpdate);
    }

    this.swUpdate.unrecoverable.pipe(
      take(1),
    ).subscribe(() => {
      if ('caches' in window) {
        caches.keys().then(names => names.forEach(n => caches.delete(n)));
      }
      location.replace(location.origin);
    });


    this.swUpdate.versionUpdates.pipe(
      filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      switchMap(() => this.http.get<{ version: string }>('/version.json')),
      take(1),
    ).subscribe({
      next: ({ version }) => {
        this.ngbModal.open(NewVersionConfirmationDialog, {
          size: 'md',
          backdrop: 'static',
          keyboard: false,
          windowClass: 'custom-modal'
        }).componentInstance.args = {
          version,
          confirm: () => {
            location.replace(location.origin);
          }
        }
      },
      error: (err) => console.error('Version check failed:', err)
    });
  }
}
